import { Company, Role, User } from "@adsk/alloy-provider";
import {
  useCompaniesEntitiesMapByOxygenId,
  useRolesEntitiesMapByOxygenId,
  useUsersEntitiesMapByOxygenId,
} from "@adsk/acc-issues-common/hooks/data/projectMembersHooks";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  FormattedDisplayUserOptions,
  useFormattedUser,
} from "@adsk/alloy-provider";
import { useGetCompanyIdToUserNameCountMap } from "@adsk/acc-issues-common/utils/user/oxygenUtils";
import { useCallback, useMemo } from "react";

interface CompanyIdToUserNameCount {
  [companyId: string]: { [userName: string]: number };
}

interface FormatOption {
  ignoreDeletedFormat?: FormattedDisplayUserOptions["ignoreDeletedFormat"];
  showEmailOnDuplicateName?: boolean;
}
const addEmailForDuplicateNames = ({
  userName,
  userEmail,
  userCompanyId,
  companyIdToUserNameCountMap,
}: {
  userName: string;
  userEmail: string;
  userCompanyId?: string;
  companyIdToUserNameCountMap: CompanyIdToUserNameCount;
}) => {
  const companyId = userCompanyId ?? "NO_COMPANY_ID";
  const userCount = companyIdToUserNameCountMap[companyId]?.[userName] ?? 0;
  const transformedName =
    userCount > 1 ? `${userName} - ${userEmail}` : userName;
  return transformedName;
};

export const useGetUserByOxygenId = (
  userFormatOption: FormatOption = {
    ignoreDeletedFormat: false,
    showEmailOnDuplicateName: false,
  }
) => {
  const { ignoreDeletedFormat, showEmailOnDuplicateName } = userFormatOption;
  const { formatDisplayUser } = useFormattedUser();
  const usersEntitiesMap = useUsersEntitiesMapByOxygenId();
  const memoizedUsers = useMemo(
    () => Object.values(usersEntitiesMap),
    [usersEntitiesMap]
  );
  const companyIdToUserNameCountMap =
    useGetCompanyIdToUserNameCountMap(memoizedUsers);

  return useCallback(
    (oxygenId: string): User => {
      const { displayName = "", userDetails = null } = formatDisplayUser(
        oxygenId,
        {
          ignoreDeletedFormat,
        }
      );
      let transformedName = displayName;
      if (showEmailOnDuplicateName && userDetails) {
        transformedName = addEmailForDuplicateNames({
          userName: displayName,
          userEmail: userDetails.email,
          userCompanyId: userDetails.companyId,
          companyIdToUserNameCountMap,
        });
      }
      const userInfo = {
        ...(userDetails ?? {}),
        name: transformedName,
      };
      return (userInfo.name ? userInfo : null) as User;
    },
    [
      formatDisplayUser,
      ignoreDeletedFormat,
      showEmailOnDuplicateName,
      companyIdToUserNameCountMap,
    ]
  );
};

export const useGetProjectActorByOxygenId = (
  formatOptions: FormatOption = {}
) => {
  // Destructure formatOptions to avoid passing undefined to the hook
  const { ignoreDeletedFormat = false, showEmailOnDuplicateName = false } =
    formatOptions ?? {};

  // Initialize hooks for translations and data fetching
  const { T } = useTranslations();
  const getUserData = useGetUserByOxygenId({
    ignoreDeletedFormat,
    showEmailOnDuplicateName,
  });
  const rolesEntitiesMap = useRolesEntitiesMapByOxygenId();
  const companiesEntitiesMap = useCompaniesEntitiesMapByOxygenId();

  return useCallback(
    (oxygenId: string) => {
      // Initialize former actor with default name
      const nonMemberActor = {
        name: T.translate("activityLog.nonMember") as string,
      };

      // Initialize actor variable
      let actor: User | Role | Company | null;

      // Return null if oxygenId is not provided
      if (!oxygenId) return null;

      // Get company data by oxygenId
      actor = companiesEntitiesMap[oxygenId];

      // If actor is not found, try to find the role
      if (!actor) {
        actor = rolesEntitiesMap[oxygenId];
      }

      // If role is not found, try to find the user
      if (!actor) {
        const user = getUserData(oxygenId);
        actor = user?.name ? user : null;
      }

      // Return actor if found, otherwise return the non member user

      return actor || nonMemberActor;
    },
    [T, companiesEntitiesMap, rolesEntitiesMap, getUserData]
  );
};
